/**
 * PublicBroadcastPage — the web version of a sent broadcast.
 *
 * Step 6a of BROADCASTS_AND_AUDIENCES_PLAN.md. This is where a "read the full
 * thing" link from an SMS lands, and where a stranger clicking a social post
 * ends up. It is deliberately NOT the member portal: `show_as_news` already
 * covers the in-app case, behind auth, which a marketing contact cannot reach.
 *
 * Unauthenticated. The slug is the only credential, so the page shows the
 * shared body and nothing about who else received it.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { useTheme } from "../../context/ThemeContext";
import {
  getPageBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
} from "../../utils/themeUtils";
import { getPublicBroadcast, PublicBroadcast } from "../../api/publicBroadcastAPI";

const PublicBroadcastPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { slug } = useParams<{ slug: string }>();

  const [broadcast, setBroadcast] = useState<PublicBroadcast | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Search engines should not index a link that was handed to specific
    // people. Unguessable is not the same as intended-for-everyone, and a
    // social post makes the URL public without making it a landing page.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }
      const res = await getPublicBroadcast(slug);
      if (cancelled) return;
      if (res.success && res.data) {
        setBroadcast(res.data);
        document.title = res.data.subject || "Message";
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${getPageBackground(isDarkMode)}`}
      >
        <div className="w-8 h-8 border-4 border-t-transparent border-gray-400 dark:border-gray-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !broadcast) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center px-6 ${getPageBackground(isDarkMode)}`}
      >
        <div className="text-center max-w-md">
          <h1 className={`text-2xl font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>
            This message isn't available
          </h1>
          {/* An unknown slug, an unsent broadcast and a revoked link are one
              message on purpose — telling a stranger which it is tells them
              something about content they can't see. */}
          <p className={getSecondaryText(isDarkMode)}>
            The link may be incorrect, or the message may no longer be shared.
          </p>
        </div>
      </div>
    );
  }

  const sentOn = broadcast.sent_at
    ? new Date(broadcast.sent_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className={`min-h-screen py-10 px-4 ${getPageBackground(isDarkMode)}`}>
      <article
        className={`max-w-2xl mx-auto rounded-lg border p-6 sm:p-8 ${
          isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${getPrimaryText(isDarkMode)}`}>
          {broadcast.subject}
        </h1>

        {(sentOn || broadcast.sent_by_name) && (
          <p className={`text-sm mb-6 ${getMutedText(isDarkMode)}`}>
            {[broadcast.sent_by_name, sentOn].filter(Boolean).join(" · ")}
          </p>
        )}

        {broadcast.image_url && (
          <img
            src={broadcast.image_url}
            alt=""
            className="w-full h-auto rounded mb-6"
          />
        )}

        {/* The stored body is the rendered email HTML, so it is sanitized here
            rather than trusted — it passes through a tenant-authored template
            and lands on a page anyone can open. */}
        <div
          className={`prose max-w-none ${isDarkMode ? "prose-invert" : ""}`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(broadcast.message || "") }}
        />
      </article>
    </div>
  );
};

export default PublicBroadcastPage;
